/**
 * Default extra quantities for storefront cart lines driven by products.json.
 * qtyMin / qtyDefault / qtyMax are applied per parent unit; extra line quantity scales
 * with parent line `quantity`.
 */

export type CatalogExtraProduct = {
  productUuid: string;
  qtyMin: number;
  qtyMax: number;
  qtyDefault: number | null;
};

export type CartLineForExtraSync = {
  productUuid: string;
  quantity: number;
  maxAvailable?: number;
  eventHash?: string;
  start?: string;
  cartLineSource?: "autoExtra";
  parentProductUuids?: string[];
  invalid?: boolean;
};

export type ProductWithExtras = {
  productUuid: string;
  extraProducts?: CatalogExtraProduct[];
};

export function getEffectiveExtraProductQty(
  leaf: Pick<CatalogExtraProduct, "qtyMin" | "qtyMax" | "qtyDefault">,
  parentQuantity: number
): number {
  const defaultVal = leaf.qtyDefault ?? 0;
  const minVal = leaf.qtyMin ?? 0;
  const maxVal = leaf.qtyMax ?? 0;

  let perParentUnit = Math.max(defaultVal, minVal);
  if (perParentUnit <= 0) {
    return 0;
  }
  // `max` is per parent cart unit, not a cap on the summed extra line quantity.
  if (maxVal > 0) {
    perParentUnit = Math.min(perParentUnit, maxVal);
  }

  return Math.floor(perParentUnit * parentQuantity);
}

export function buildAutoExtraDemandByProductUuid(
  extraProducts: CatalogExtraProduct[] | undefined,
  parentQuantity: number
): Map<string, number> {
  const m = new Map<string, number>();
  if (!extraProducts?.length) {
    return m;
  }
  for (const extraProduct of extraProducts) {
    if (
      typeof extraProduct.productUuid !== "string" ||
      extraProduct.productUuid.length === 0
    ) {
      continue;
    }
    const q = getEffectiveExtraProductQty(extraProduct, parentQuantity);
    if (q <= 0) {
      continue;
    }
    m.set(extraProduct.productUuid, (m.get(extraProduct.productUuid) ?? 0) + q);
  }
  return m;
}

function productHasExtras(
  product: ProductWithExtras | undefined
): boolean {
  return !!(
    product &&
    Array.isArray(product.extraProducts) &&
    product.extraProducts.length > 0
  );
}

function baseCartLineMergeKey(line: CartLineForExtraSync): string {
  return `${line.productUuid}|${line.eventHash ?? ""}`;
}

function normalizeParentProductUuids(
  parentProductUuids?: Array<string | null | undefined>
): string[] | undefined {
  if (!parentProductUuids?.length) {
    return undefined;
  }
  const unique = [
    ...new Set(
      parentProductUuids.filter(
        (uuid): uuid is string =>
          typeof uuid === "string" && uuid.trim().length > 0
      )
    ),
  ];
  return unique.length ? unique : undefined;
}

function lineRepresentsAutoExtra(
  line: Pick<CartLineForExtraSync, "cartLineSource" | "parentProductUuids">
): boolean {
  return (
    line.cartLineSource === "autoExtra" ||
    (line.parentProductUuids?.length ?? 0) > 0
  );
}

function mergeUniqueIds(a?: string[], b?: string[]): string[] | undefined {
  return normalizeParentProductUuids([...(a ?? []), ...(b ?? [])]);
}

/** Stable cart line identity for merge/dedupe and React list keys. */
export function cartLineMergeKey(line: CartLineForExtraSync): string {
  const baseKey = baseCartLineMergeKey(line);
  if (!lineRepresentsAutoExtra(line)) {
    return baseKey;
  }

  const parentKey = normalizeParentProductUuids(line.parentProductUuids)
    ?.slice()
    .sort()
    .join(",");

  return parentKey ? `${baseKey}|parents:${parentKey}` : baseKey;
}

type OrderProductMatchFields = {
  productUuid?: string;
  eventHash?: string | null;
  eventStart?: string | null;
  parentUuid?: string | null;
  parentProductUuids?: string[];
};

function orderProductParentProductUuids(
  op: Pick<OrderProductMatchFields, "parentUuid" | "parentProductUuids">
): string[] | undefined {
  return normalizeParentProductUuids([
    ...(op.parentProductUuids ?? []),
    op.parentUuid ?? undefined,
  ]);
}

function eventStartsMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const aMs = new Date(a).getTime();
  const bMs = new Date(b).getTime();
  if (Number.isFinite(aMs) && Number.isFinite(bMs)) {
    return aMs === bMs;
  }
  return a === b;
}

/**
 * Merge lines that share id + event (same as add-to-cart merge key).
 */
export function dedupeMergedCartLines(
  items: CartLineForExtraSync[]
): CartLineForExtraSync[] {
  const m = new Map<string, CartLineForExtraSync>();
  for (const it of items) {
    const key = cartLineMergeKey(it);
    const o = m.get(key);
    if (!o) {
      m.set(key, { ...it });
      continue;
    }
    const mergedParentProductUuids = mergeUniqueIds(
      o.parentProductUuids,
      it.parentProductUuids
    );
    const keepAutoSemantics =
      lineRepresentsAutoExtra(o) ||
      lineRepresentsAutoExtra(it) ||
      (mergedParentProductUuids?.length ?? 0) > 0;
    m.set(key, {
      ...o,
      quantity: o.quantity + it.quantity,
      cartLineSource: keepAutoSemantics ? "autoExtra" : undefined,
      parentProductUuids: mergedParentProductUuids,
    });
  }
  return [...m.values()];
}

/**
 * Drops previous auto-generated extra lines and rebuilds them from current parent lines.
 */
export function resyncAutoExtraCartLines(
  items: CartLineForExtraSync[],
  products: ProductWithExtras[]
): CartLineForExtraSync[] {
  // Auto-extra lines are fully derived from current parent catalog definitions.
  // Keep only parent/regular lines and regenerate auto extras from scratch.
  const preserved = items.filter((i) => !lineRepresentsAutoExtra(i));
  const productsByUuid = new Map<string, ProductWithExtras>();
  for (const product of products) {
    productsByUuid.set(product.productUuid, product);
  }

  const autoLines: CartLineForExtraSync[] = [];
  const autoLineBaseKeys = new Set<string>();

  for (const item of preserved) {
    if (item.invalid === true) {
      continue;
    }
    if (!item.productUuid) {
      continue;
    }
    const product = productsByUuid.get(item.productUuid);
    if (!productHasExtras(product)) {
      continue;
    }
    const qtyByExtraProduct = buildAutoExtraDemandByProductUuid(
      product?.extraProducts,
      item.quantity
    );

    for (const [extraProductUuid, qty] of qtyByExtraProduct.entries()) {
      if (qty <= 0) {
        continue;
      }
      const autoLine = {
        productUuid: extraProductUuid,
        quantity: qty,
        maxAvailable: item.maxAvailable,
        eventHash: item.eventHash,
        start: item.start,
        cartLineSource: "autoExtra" as const,
        parentProductUuids: item.productUuid ? [item.productUuid] : [],
      };
      const hasMatchingInvalidLine = preserved.some(
        (line) =>
          line.invalid === true &&
          line.productUuid === autoLine.productUuid &&
          orderLineEventsMatch(line, { eventHash: autoLine.eventHash ?? null })
      );
      if (hasMatchingInvalidLine) {
        continue;
      }
      autoLineBaseKeys.add(baseCartLineMergeKey(autoLine));
      autoLines.push(autoLine);
    }
  }

  const withoutStaleRegularExtraMirrors = preserved.filter((line) => {
    if (lineRepresentsAutoExtra(line)) {
      return false;
    }
    return !autoLineBaseKeys.has(baseCartLineMergeKey(line));
  });

  // A product may be both a standalone sellable product (including a variant
  // target) and an extra for another product. Preserve independently added
  // lines; only auto-generated lines are rebuilt above.
  return dedupeMergedCartLines([
    ...withoutStaleRegularExtraMirrors,
    ...autoLines,
  ]);
}

export function itemHasEventFlags(
  item: Pick<CartLineForExtraSync, "eventHash">
): boolean {
  return !!(item.eventHash);
}

/** Order API row vs cart line: same location product and compatible event context. */
export function orderProductMatchesCartLine(
  item: CartLineForExtraSync,
  op: OrderProductMatchFields
): boolean {
  if (!item.productUuid || !op.productUuid || item.productUuid !== op.productUuid) {
    return false;
  }
  if (!itemHasEventFlags(item)) {
    if (item.invalid === true) {
      const itemParentProductUuids = normalizeParentProductUuids(
        item.parentProductUuids
      );
      if (!itemParentProductUuids?.length) {
        return true;
      }
      const orderParents = orderProductParentProductUuids(op);
      if (!orderParents?.length) {
        return true;
      }
      return orderParents.some((parentUuid) =>
        itemParentProductUuids.includes(parentUuid)
      );
    }
    return !op.eventHash;
  }
  if (item.eventHash) {
    if (op.eventHash !== item.eventHash) {
      return false;
    }
  } else {
    return false;
  }

  const itemParentProductUuids = normalizeParentProductUuids(
    item.parentProductUuids
  );
  if (!itemParentProductUuids?.length) {
    return true;
  }

  const orderParents = orderProductParentProductUuids(op);
  if (!orderParents?.length) {
    return true;
  }

  return orderParents.some((parentUuid) =>
    itemParentProductUuids.includes(parentUuid)
  );
}

export function findMatchingOrderProductForCartLine<
  T extends OrderProductMatchFields,
>(
  item: CartLineForExtraSync,
  orderProducts: T[]
): T | undefined {
  const baseMatches = orderProducts.filter((op) =>
    orderProductMatchesCartLine(item, op)
  );
  if (baseMatches.length <= 1) {
    return baseMatches[0];
  }

  const itemParents = normalizeParentProductUuids(item.parentProductUuids);
  if (!itemParents?.length) {
    return baseMatches[0];
  }

  const parentAwareMatches = baseMatches.filter((op) => {
    const orderParents = orderProductParentProductUuids(op);
    if (!orderParents?.length) {
      return false;
    }
    return orderParents.some((parentUuid) => itemParents.includes(parentUuid));
  });

  return parentAwareMatches[0] ?? baseMatches[0];
}

function orderLineEventsMatch(
  item: Pick<CartLineForExtraSync, "eventHash" | "start"> & {
    eventStart?: string | null;
  },
  op: {
    eventHash?: string | null;
    eventStart?: string | null;
  }
): boolean {
  if (item.eventHash || op.eventHash) {
    return !!item.eventHash && !!op.eventHash && item.eventHash === op.eventHash;
  }
  const itemStart = item.eventStart ?? item.start;
  const opStart = op.eventStart;
  if (opStart) {
    return eventStartsMatch(itemStart, opStart);
  }
  return true;
}

/** Returns true when parent catalog line currently declares this extra product. */
function productDeclaresExtra(
  parentProduct: ProductWithExtras | undefined,
  extraProductUuid: string
): boolean {
  return !!parentProduct?.extraProducts?.some(
    (extra) => extra.productUuid === extraProductUuid
  );
}

/** Infer auto-extra metadata for confirmed order lines when cart snapshot is unavailable. */
export function inferOrderProductExtraMetadata(
  op: {
    productUuid?: string;
    eventHash?: string | null;
    eventStart?: string | null;
  },
  orderProducts: Array<{
    productUuid?: string;
    eventHash?: string | null;
    eventStart?: string | null;
  }>,
  products: ProductWithExtras[]
):
  | Pick<CartLineForExtraSync, "cartLineSource" | "parentProductUuids">
  | undefined {
  if (!op.productUuid) {
    return undefined;
  }
  const productsByUuid = new Map<string, ProductWithExtras>();
  for (const product of products) {
    productsByUuid.set(product.productUuid, product);
  }

  const parentIds: string[] = [];
  for (const parentOp of orderProducts) {
    if (!parentOp.productUuid || parentOp.productUuid === op.productUuid) {
      continue;
    }
    const parentProduct = productsByUuid.get(parentOp.productUuid);
    if (!productDeclaresExtra(parentProduct, op.productUuid)) {
      continue;
    }
    if (
      !orderLineEventsMatch(
        {
          eventHash: op.eventHash ?? undefined,
          eventStart: op.eventStart,
        },
        parentOp
      )
    ) {
      continue;
    }
    parentIds.push(parentOp.productUuid);
  }

  if (parentIds.length === 0) {
    return undefined;
  }

  return {
    cartLineSource: "autoExtra",
    parentProductUuids: parentIds,
  };
}

export function cartLineRetainedByOrderProducts(
  item: CartLineForExtraSync,
  orderProducts: Array<{
    productUuid?: string;
    eventHash?: string | null;
    parentUuid?: string | null;
    parentProductUuids?: string[];
  }>
): boolean {
  const itemParentProductUuids = normalizeParentProductUuids(
    item.parentProductUuids
  );
  if (lineRepresentsAutoExtra(item) && itemParentProductUuids?.length) {
    const parentsStillInOrder = itemParentProductUuids.some((parentUuid) =>
      orderProducts.some((op) => op.productUuid === parentUuid)
    );
    if (!parentsStillInOrder) {
      return orderProducts.some((op) =>
        orderProductMatchesCartLine(item, op)
      );
    }
  }

  if (
    orderProducts.some((op) =>
      orderProductMatchesCartLine(item, op)
    )
  ) {
    return true;
  }
  if (item.invalid === true) {
    const sameProduct = orderProducts.filter(
      (op) => op.productUuid === item.productUuid
    );
    if (sameProduct.length === 0) {
      return false;
    }
    const itemParents = normalizeParentProductUuids(item.parentProductUuids);
    if (!itemParents?.length) {
      return true;
    }
    return sameProduct.some((op) => {
      const orderParents = orderProductParentProductUuids(op);
      if (!orderParents?.length) {
        return true;
      }
      return orderParents.some((parentUuid) => itemParents.includes(parentUuid));
    });
  }
  return false;
}
